import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CloseReason,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  GridProps,
  IconButton,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';
import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { teceoGrayPrimary } from '../../Theme';
import {
  getModalContentXPadding,
  getModalContentYPadding,
} from '../../modules/orders/contract/utils';
import useTeceoStyles from '../../useTeceoStyles';
import useWidth from '../Hooks/useWidth';

type UseStylesProps = {
  disableDialogActionsPadding?: boolean;
  dialogTitlePX: number;
  dialogTitlePY: number;
  disableTitleDivider?: boolean;
};

const useStyles = makeStyles<UseStylesProps>()(
  (
    theme: Theme,
    { dialogTitlePX, dialogTitlePY, disableDialogActionsPadding, disableTitleDivider },
  ) => ({
    paper: {
      position: 'relative',
      borderRadius: theme.spacing(2),
      padding: 0,
    },
    fullScreenPaper: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    dialogActions: {
      padding: disableDialogActionsPadding ? 0 : `${theme.spacing(1)}px ${theme.spacing(4)}px`,
      overflowX: 'hidden',
    },
    fullScreenDialogActions: {
      padding: disableDialogActionsPadding ? 0 : `${theme.spacing(2)}px ${theme.spacing(1)}px`,
      overflowX: 'hidden',
    },
    dialogContentMobile: {
      display: 'flex',
      flexDirection: 'column',
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    },
    dialogTitlePadding: {
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(dialogTitlePY)}px ${theme.spacing(dialogTitlePX)}px`,
      },
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
    },
    divider: {
      borderBottom: disableTitleDivider ? 'none' : '1px solid rgba(0,0,0, 0.16)',
    },
    title: {
      textTransform: 'lowercase',
      textAlign: 'left',
    },
    iconButton: {
      marginRight: theme.spacing(2),
      color: theme.palette.action.active,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    icon: {
      fontSize: theme.spacing(3),
      color: teceoGrayPrimary,
    },
    commentsContainer: {
      marginTop: theme.spacing(2),
    },
    scrollXDisabled: {
      overflowX: 'hidden',
    },
    scrollYDisabled: {
      overflowY: 'hidden',
    },
    disabledDialogContentPadding: {
      padding: 0,
    },
  }),
);

type TeceoModalProps = {
  title?: string;
  titleSecondary?: boolean;
  titleCustomClassName?: string;
  titleProps?: TypographyProps & { fontWeight?: number };
  subtitle?: string;
  open: boolean;
  onClose: (reason: CloseReason) => void;
  children: React.ReactNode;
  headerProps?: GridProps;
  leftIcon?: React.ReactNode;
  leftIconProps?: GridProps;
  onExited?: () => void;
  disableCloseButton?: boolean;
  dialogProps?: Partial<DialogProps>;
  dialogActions?: React.ReactNode;
  disableXScrollbar?: boolean;
  disableYScrollbar?: boolean;
  disableDialogContentPadding?: boolean;
  disableDialogActionsPadding?: boolean;
  dialogTitleStyle?: React.CSSProperties;
  disableTitleDivider?: boolean;
};

/** @deprecated Use `components/v2/ui/Modal`, que substitui este componente no projeto **/
export const TeceoModal = ({
  title,
  titleSecondary,
  titleCustomClassName,
  subtitle,
  open,
  titleProps = {},
  onClose,
  dialogProps = {},
  dialogActions,
  headerProps,
  children,
  onExited,
  leftIcon,
  leftIconProps,
  disableCloseButton,
  disableXScrollbar,
  disableYScrollbar,
  disableDialogContentPadding,
  disableDialogActionsPadding,
  disableTitleDivider,
  dialogTitleStyle = {},
}: TeceoModalProps) => {
  const { mobileWidth, smallWidth } = useWidth();
  const px = getModalContentXPadding(smallWidth);
  const py = getModalContentYPadding(smallWidth);
  const { classes, cx } = useStyles({
    disableDialogActionsPadding,
    dialogTitlePX: px,
    dialogTitlePY: py,
    disableTitleDivider,
  });
  const { classes: teceoClasses } = useTeceoStyles();
  const { children: headerChildren, ...restHeaderProps } = headerProps || {};

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleClose = (reason: CloseReason) => {
    document.body.style.overflow = 'unset';
    onClose(reason);
  };

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      open={open}
      onClose={handleClose}
      onMouseUp={(event) => event.stopPropagation()}
      {...dialogProps}
      slotProps={{
        transition: {
          onExited,
        },
      }}
    >
      <Grid container alignItems="center" {...restHeaderProps}>
        {leftIcon && (
          <Box display="flex" {...leftIconProps}>
            {leftIcon}
          </Box>
        )}

        <Grid>
          {title && (
            <DialogTitle style={dialogTitleStyle} className={classes.dialogTitlePadding}>
              <Typography
                align="center"
                color={titleSecondary ? 'textSecondary' : 'textPrimary'}
                className={titleCustomClassName || cx(classes.title, teceoClasses.boldText)}
                {...titleProps}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography align="center" color="textSecondary" className={cx(classes.title)}>
                  {subtitle}
                </Typography>
              )}
            </DialogTitle>
          )}
        </Grid>
        {!disableCloseButton && (
          <Grid style={{ marginLeft: 'auto' }}>
            {headerChildren && headerChildren}
            <IconButton
              size="small"
              onClick={() => handleClose('toggle')}
              className={classes.iconButton}
            >
              <CloseIcon className={classes.icon} color="action" />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <div className={classes.divider} />
      <DialogContent
        classes={{
          root: cx(
            mobileWidth && classes.dialogContentMobile,
            disableXScrollbar && classes.scrollXDisabled,
            disableYScrollbar && classes.scrollYDisabled,
            disableDialogContentPadding && classes.disabledDialogContentPadding,
          ),
        }}
      >
        {children}
      </DialogContent>
      {dialogActions && (
        <DialogActions
          classes={{
            root: dialogProps?.fullScreen ? classes.fullScreenDialogActions : classes.dialogActions,
          }}
        >
          {dialogActions}
        </DialogActions>
      )}
    </Dialog>
  );
};
